import React, { useState, useCallback } from "react";
import AppSelect from "../ui/AppSelect";

/**
 * Cancellation capture, shared by every order-details screen.
 *
 * Every `handleCancelOrder` on the dashboard used to flip `delivery_status` to
 * REJECTED behind a yes/no modal — no reason, no actor. A cancelled order was
 * then indistinguishable from a vendor rejection or a staff mistake, and
 * finance had nothing to reconcile a refund against.
 *
 * Exposed as a hook + a fields block rather than a whole modal, so each screen
 * keeps its own react-bootstrap `<Modal>` chrome and styling. Drop
 * `<CancelOrderFields state={...} />` into the existing Modal.Body.
 *
 * MIRROR of ORDER_CANCELLATION_REASONS in
 * `services/orders/orderCancellationService.js`, which is the source of truth
 * and re-validates every code server-side. The two are pinned equal by
 * `__tests__/orderCancellationAudit.test.js` — add a reason in BOTH places.
 */
export const CANCELLATION_REASONS = [
  { code: "VENDOR_UNREACHABLE", label: "Vendor unreachable" },
  { code: "VENDOR_CANNOT_FULFIL", label: "Vendor can't fulfil the order" },
  { code: "OUT_OF_STOCK", label: "Item out of stock" },
  { code: "CUSTOMER_REQUESTED", label: "Customer asked to cancel" },
  { code: "CUSTOMER_UNREACHABLE", label: "Customer unreachable" },
  { code: "DUPLICATE_ORDER", label: "Duplicate order" },
  { code: "PAYMENT_FAILED", label: "Payment failed or not received" },
  { code: "NO_RIDER", label: "No rider available" },
  { code: "OUTSIDE_DELIVERY_AREA", label: "Outside the delivery area" },
  { code: "VENDOR_CLOSED", label: "Vendor closed / outside operating hours" },
  { code: "TEST_ORDER", label: "Test order" },
  { code: "OTHER", label: "Other (explain in the note)" },
];

/**
 * Owns the reason/note state and its validation.
 *   validate() → true when it may be submitted; also reveals the error text
 *   payload()  → { reason, note } for the action
 *   reset()    → call when the modal closes
 */
export const useCancellationReason = () => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);

  const noteRequired = reason === "OTHER";
  const noteMissing = noteRequired && !note.trim();
  const valid = !!reason && !noteMissing;

  const validate = useCallback(() => {
    setTouched(true);
    return !!reason && !(reason === "OTHER" && !note.trim());
  }, [reason, note]);

  const reset = useCallback(() => { setReason(""); setNote(""); setTouched(false); }, []);
  const payload = useCallback(() => ({ reason, note: note.trim() }), [reason, note]);

  return { reason, setReason, note, setNote, touched, noteRequired, noteMissing, valid, validate, reset, payload };
};

const LABEL = {
  display: "block", fontSize: 11, fontWeight: 800, letterSpacing: ".4px",
  textTransform: "uppercase", color: "#475467", margin: "14px 0 6px",
};

/** The reason picker + note. `state` is what useCancellationReason() returned. */
export const CancelOrderFields = ({ state, isPaid = false, idPrefix = "cancel" }) => {
  const { reason, setReason, note, setNote, touched, noteRequired, noteMissing } = state;
  return (
    <div style={{ textAlign: "left" }}>
      <div style={{ fontSize: 12.5, color: "#667085", lineHeight: 1.5 }}>
        The reason and your name are stored on this order permanently, for audit.
        {isPaid ? <strong style={{ color: "#b26a00" }}> This order is already paid — a refund is handled separately.</strong> : null}
      </div>

      <label htmlFor={`${idPrefix}-reason`} style={LABEL}>Reason *</label>
      <AppSelect
        id={`${idPrefix}-reason`}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        style={{ width: "100%", height: 40, borderRadius: 8, border: `1px solid ${touched && !reason ? "#d92d20" : "#d0d5dd"}`, padding: "0 10px", fontSize: 13.5, background: "#fff", color: "#101828" }}
      >
        <option value="">— choose a reason —</option>
        {CANCELLATION_REASONS.map((r) => (
          <option key={r.code} value={r.code}>{r.label}</option>
        ))}
      </AppSelect>
      {touched && !reason && (
        <div style={{ fontSize: 11.5, color: "#d92d20", marginTop: 4 }}>Pick a reason — it goes on the audit record.</div>
      )}

      <label htmlFor={`${idPrefix}-note`} style={LABEL}>Note {noteRequired ? "*" : "(optional)"}</label>
      <textarea
        id={`${idPrefix}-note`}
        rows={3}
        value={note}
        maxLength={1000}
        onChange={(e) => setNote(e.target.value)}
        placeholder={noteRequired ? "Required when the reason is Other" : "Anything the next person reading this record should know"}
        style={{ width: "100%", borderRadius: 8, border: `1px solid ${touched && noteMissing ? "#d92d20" : "#d0d5dd"}`, padding: "9px 10px", fontSize: 13, resize: "vertical", fontFamily: "inherit" }}
      />
      {touched && noteMissing && (
        <div style={{ fontSize: 11.5, color: "#d92d20", marginTop: 4 }}>Explain what happened.</div>
      )}
    </div>
  );
};

/** Read-only audit record, shown on an order that has already been cancelled. */
export const CancellationRecord = ({ cancellation }) => {
  if (!cancellation?.cancelledAt) return null;
  const who = cancellation.cancelledByName || cancellation.cancelledByEmail || cancellation.cancelledById || "Unknown user";
  const when = new Date(cancellation.cancelledAt);
  return (
    <div style={{ border: "1px solid #fecdca", background: "#fffbfa", borderRadius: 10, padding: "12px 14px", marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".4px", textTransform: "uppercase", color: "#b42318" }}>Cancellation record</div>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: "#101828", marginTop: 6 }}>
        {cancellation.reasonLabel || cancellation.reason}
      </div>
      {cancellation.note ? (
        <div style={{ fontSize: 12.5, color: "#475467", marginTop: 4, whiteSpace: "pre-wrap" }}>{cancellation.note}</div>
      ) : null}
      <div style={{ fontSize: 11.5, color: "#667085", marginTop: 8 }}>
        {who}{cancellation.cancelledByRole ? ` · ${cancellation.cancelledByRole}` : ""}
        {" · "}{Number.isFinite(when.getTime()) ? when.toLocaleString() : ""}
      </div>
      <div style={{ fontSize: 11.5, color: "#667085", marginTop: 2 }}>
        Was {cancellation.previousDeliveryStatus || "—"} at the time
        {cancellation.wasPaidAtCancellation ? " · order was already PAID" : " · order was unpaid"}
      </div>
    </div>
  );
};

export default CancelOrderFields;
